class R13TrackBundle extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.sectionId = this.dataset.sectionId;
    this.currentRole = this.dataset.currentRole;
    this.currentSize = this.dataset.currentSize;
    this.currentOptionIndex = Number(this.dataset.currentOptionIndex || 1);
    this.currentProductSizeOptionIndex = Number(this.dataset.currentProductSizeOptionIndex || 1);
    this.addonOptionIndex = Number(this.dataset.addonOptionIndex || 2);
    this.bundleType = this.dataset.initialBundleType;
    this.submitLabel = this.dataset.submitLabel || 'Add Complete Set';
    this.soldOutLabel = this.dataset.soldOutLabel || 'Set Sold Out';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    this.bundleProducts = this.parseJson('[data-bundle-products]') || {
      pants: this.parseJson('[data-bundle-product="pants"]'),
      shorts: this.parseJson('[data-bundle-product="shorts"]'),
    };
    this.bundleAddonRoles = this.parseJson('[data-bundle-addon-roles]') || {};
    this.componentProducts = this.parseJson('[data-component-products]') || {};
    this.componentUrls = this.parseJson('[data-component-urls]') || {};
    this.componentLabels = this.parseJson('[data-component-labels]') || {};
    this.variantInput = this.querySelector('input[name="id"]');
    this.form = this.querySelector('form');
    this.mainProductForm = document.getElementById(`product-form-${this.sectionId}`);
    this.mainVariantInput = this.mainProductForm?.querySelector('input[name="id"]');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.submitButton = this.querySelector('.r13-track-bundle__submit');
    this.submitButtonText = this.submitButton?.querySelector('span');
    this.spinner = this.submitButton?.querySelector('.loading__spinner');
    this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
    this.errorMessage = this.querySelector('.product-form__error-message');
    this.addonImage = this.querySelector('[data-addon-image]');
    this.addonLink = this.querySelector('[data-addon-link]');
    this.addonTitle = this.querySelector('[data-addon-title]');
    this.addonSizeLabel = this.querySelector('[data-addon-size-label]');
    this.priceNode = this.querySelector('[data-bundle-price]');
    this.compareNode = this.querySelector('[data-bundle-compare]');
    this.savingsNode = this.querySelector('[data-bundle-savings]');

    const currentProductSizeOptionIndex = this.getProductSizeOptionIndex(this.componentProducts[this.currentRole]);
    if (currentProductSizeOptionIndex) this.currentProductSizeOptionIndex = currentProductSizeOptionIndex;

    this.querySelectorAll('[data-bundle-type-button]').forEach((button) => {
      button.addEventListener('click', () => {
        this.setBundleType(button.dataset.bundleTypeButton);
      });
    });

    this.querySelectorAll('input[name^="r13-bundle-addon-size"]').forEach((input) => {
      input.addEventListener('change', () => this.updateSelectedVariant());
    });

    this.mainVariantInput?.addEventListener('change', () => {
      this.syncCurrentSize();
      this.updateSelectedVariant({ syncCurrentSize: false });
    });

    this.form?.addEventListener('submit', this.onSubmit.bind(this));

    if (window.PUB_SUB_EVENTS && typeof window.subscribe === 'function') {
      this.variantUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.sectionId !== this.sectionId || !data.variant) return;
        this.updateCurrentSizeFromVariant(data.variant);
        this.updateSelectedVariant({ syncCurrentSize: false });
      });
    }

    this.setBundleType(this.bundleType);
  }

  onSubmit(event) {
    event.preventDefault();
    this.syncCurrentSize();
    this.updateSelectedVariant({ syncCurrentSize: false });

    if (!this.form || !this.submitButton || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') {
      return;
    }

    this.handleErrorMessage();
    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.classList.add('loading');
    this.spinner?.classList.remove('hidden');

    const config = typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { Accept: 'application/json' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this.form);
    if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        this.cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      this.cart.setActiveElement?.(document.activeElement);
    }
    config.body = formData;

    let addedToCart = false;

    fetch(window.routes?.cart_add_url || '/cart/add.js', config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description || response.message);
          return;
        }

        if (!this.cart) {
          window.location = window.routes?.cart_url || '/cart';
          return;
        }

        if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'r13-track-bundle',
            productVariantId: formData.get('id'),
            cartData: response,
          });
        }

        addedToCart = true;
        this.setCartHasItems();
        this.cart.renderContents?.(response);
        requestAnimationFrame(() => this.setCartHasItems());
      })
      .catch((error) => {
        console.error(error);
        this.handleErrorMessage('Unable to add the set. Please try again.');
      })
      .finally(() => {
        this.submitButton.classList.remove('loading');
        if (addedToCart) this.setCartHasItems();
        this.submitButton.removeAttribute('aria-disabled');
        this.spinner?.classList.add('hidden');
      });
  }

  setCartHasItems() {
    document.querySelector('cart-drawer')?.classList.remove('is-empty');
    document.querySelector('cart-drawer .drawer__inner')?.classList.remove('is-empty');
    document.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    this.cart?.classList?.remove('is-empty');
  }

  handleErrorMessage(message = false) {
    if (!this.errorMessageWrapper || !this.errorMessage) return;

    this.errorMessageWrapper.toggleAttribute('hidden', !message);
    if (message) this.errorMessage.textContent = message;
  }

  disconnectedCallback() {
    this.variantUnsubscriber?.();
  }

  parseJson(selector) {
    try {
      const node = this.querySelector(selector);
      return node ? JSON.parse(node.textContent) : null;
    } catch (error) {
      console.warn('Unable to parse bundle data', error);
      return null;
    }
  }

  getProductSizeOptionIndex(product) {
    const index = product?.options?.findIndex((option) => this.getOptionName(option).includes('size'));
    return index >= 0 ? index + 1 : 1;
  }

  getOptionName(option) {
    return String(option?.name || option || '').toLowerCase();
  }

  getRoleOptionTerms(role) {
    return {
      jacket: ['track jacket'],
      pants: ['track pants'],
      shorts: ['track shorts'],
      espresso_hoodie: ['espresso club hoodie'],
      core_shorts: ['corner shorts', '5" corner shorts', '5 inch corner shorts'],
      airbrush_shirt_primary: ['shirt 1', 'first shirt', 'primary shirt'],
      airbrush_shirt_addon: ['shirt 2', 'second shirt', 'additional shirt', 'add-on shirt', 'addon shirt', 'shirt'],
    }[role] || [role];
  }

  getBundleOptionIndexForRole(bundleProduct, role) {
    const terms = this.getRoleOptionTerms(role);
    const index = bundleProduct?.options?.findIndex((option) => {
      const name = this.getOptionName(option);
      return terms.some((term) => name.includes(term));
    });

    return index >= 0 ? index + 1 : null;
  }

  syncBundleOptionIndexes(addonRole = this.getAddonRole()) {
    const bundleProduct = this.getActiveBundleProduct();
    const currentIndex = this.getBundleOptionIndexForRole(bundleProduct, this.currentRole);
    const addonIndex = this.getBundleOptionIndexForRole(bundleProduct, addonRole);

    if (currentIndex) this.currentOptionIndex = currentIndex;
    if (addonIndex) this.addonOptionIndex = addonIndex;

    return {
      currentIndex: this.currentOptionIndex,
      addonIndex: this.addonOptionIndex,
    };
  }

  updateCurrentSizeFromVariant(variant) {
    const selectedSize = variant?.[`option${this.currentProductSizeOptionIndex}`] || variant?.option1;
    if (selectedSize) this.currentSize = String(selectedSize);
  }

  syncCurrentSize() {
    const currentProduct = this.componentProducts[this.currentRole];
    const currentVariantId = this.mainVariantInput?.value;
    const currentVariant = currentProduct?.variants?.find((variant) => String(variant.id) === String(currentVariantId));

    if (currentVariant) {
      this.updateCurrentSizeFromVariant(currentVariant);
      return;
    }

    const variantPicker = document.getElementById(`variant-selects-${this.sectionId}`);
    const checkedInput = variantPicker?.querySelector(`input[name$="-${this.currentProductSizeOptionIndex}"]:checked`);
    const selectedOption = variantPicker?.querySelectorAll('select')?.[this.currentProductSizeOptionIndex - 1]?.selectedOptions?.[0];
    const selectedSize = checkedInput?.value || selectedOption?.value;

    if (selectedSize) this.currentSize = selectedSize;
  }

  setBundleType(type) {
    if (!this.bundleProducts[type]) return;

    this.bundleType = type;
    this.querySelectorAll('[data-bundle-type-button]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.bundleTypeButton === type ? 'true' : 'false');
    });

    const addonRole = this.getAddonRole();
    this.syncBundleOptionIndexes(addonRole);
    this.updateAddonImage(addonRole);
    this.updateAddonLink(addonRole);
    this.updateAddonTitle(addonRole);
    this.updateAddonSizeLabel(addonRole);
    this.updateSizeOptions();
    this.updateSelectedVariant();
  }

  getAddonRole() {
    if (this.bundleAddonRoles[this.bundleType]) return this.bundleAddonRoles[this.bundleType];
    if (this.currentRole === 'jacket') return this.bundleType;
    return 'jacket';
  }

  getSelectedAddonSize() {
    return this.querySelector('input[name^="r13-bundle-addon-size"]:checked')?.value || this.currentSize;
  }

  getSelectedOptions() {
    const bundleProduct = this.getActiveBundleProduct();
    const addonRole = this.getAddonRole();
    const { currentIndex, addonIndex } = this.syncBundleOptionIndexes(addonRole);
    const selectedOptions = new Array(bundleProduct?.options?.length || 2);

    selectedOptions[currentIndex - 1] = this.currentSize;
    selectedOptions[addonIndex - 1] = this.getSelectedAddonSize();

    return selectedOptions;
  }

  getActiveBundleProduct() {
    return this.bundleProducts[this.bundleType];
  }

  updateSelectedVariant({ syncCurrentSize = true } = {}) {
    if (syncCurrentSize) this.syncCurrentSize();

    const bundleProduct = this.getActiveBundleProduct();
    if (!bundleProduct?.variants?.length || !this.variantInput || !this.submitButton) return;

    const selectedOptions = this.getSelectedOptions();
    const variant = bundleProduct.variants.find((item) => (
      selectedOptions.every((value, index) => !value || item[`option${index + 1}`] === value)
    ));

    if (variant) {
      this.variantInput.value = variant.id;
      this.variantInput.disabled = !variant.available;
    }

    const available = Boolean(variant?.available);
    this.submitButton.disabled = !available;
    this.submitButtonText.textContent = available ? this.submitLabel : this.soldOutLabel;
    this.updatePrice(bundleProduct, variant);
  }

  updatePrice(bundleProduct, variant) {
    if (!this.priceNode || !this.compareNode) return;

    const price = variant?.price || bundleProduct.price || 0;
    const comparePrice = this.getComparePrice(bundleProduct, price);

    this.priceNode.textContent = this.formatMoney(price);
    if (comparePrice > price) {
      this.compareNode.textContent = this.formatMoney(comparePrice);
      this.compareNode.hidden = false;
    } else {
      this.compareNode.hidden = true;
    }

    this.updateSavings(comparePrice - price);
  }

  getComparePrice(bundleProduct, price) {
    if (bundleProduct.compare_at_price && bundleProduct.compare_at_price > price) {
      return bundleProduct.compare_at_price;
    }

    const currentPrice = this.componentProducts[this.currentRole]?.price || 0;
    const addonPrice = this.componentProducts[this.getAddonRole()]?.price || 0;
    return currentPrice + addonPrice;
  }

  updateAddonImage(role) {
    if (!this.addonImage) return;

    const template = this.querySelector(`[data-component-image-template="${role}"]`);
    this.addonImage.replaceChildren(template ? template.content.cloneNode(true) : '');
  }

  updateAddonLink(role) {
    if (!this.addonLink) return;

    const title = this.componentProducts[role]?.title || role;
    this.addonLink.href = this.componentUrls[role] || this.componentProducts[role]?.url || '#';
    this.addonLink.setAttribute('aria-label', `View ${title} details`);
  }

  updateAddonTitle(role) {
    if (!this.addonTitle) return;

    this.addonTitle.textContent = this.componentProducts[role]?.title || '';
  }

  updateAddonSizeLabel(role) {
    if (!this.addonSizeLabel) return;

    this.addonSizeLabel.textContent = `Choose ${this.componentLabels[role] || role} size`;
  }

  updateSizeOptions() {
    const bundleProduct = this.getActiveBundleProduct();
    const addonRole = this.getAddonRole();
    const { addonIndex } = this.syncBundleOptionIndexes(addonRole);
    const sizeInputs = Array.from(this.querySelectorAll('input[name^="r13-bundle-addon-size"]'));
    const preferredSize = this.getSelectedAddonSize() || this.currentSize;
    const values = this.getBundleOptionValues(bundleProduct, addonIndex);

    if (!values?.length || !sizeInputs.length) return;

    sizeInputs.forEach((input) => {
      const label = input.closest('label');
      const visible = values.includes(input.value);
      label.hidden = !visible;
      input.disabled = !visible;
      input.checked = visible && input.value === preferredSize;
    });

    if (!sizeInputs.some((input) => input.checked && !input.disabled)) {
      const fallback = sizeInputs.find((input) => !input.disabled);
      if (fallback) fallback.checked = true;
    }
  }

  getBundleOptionValues(bundleProduct, optionIndex) {
    const option = bundleProduct?.options?.[optionIndex - 1];
    if (Array.isArray(option?.values) && option.values.length) return option.values;

    return Array.from(new Set(
      (bundleProduct?.variants || [])
        .map((variant) => variant?.[`option${optionIndex}`])
        .filter(Boolean)
    ));
  }

  formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    return this.moneyFormat
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(value).toLocaleString())
      .replace(/\{\{\s*amount\s*\}\}/, value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }));
  }

  formatMoneyWithoutTrailingZeros(cents) {
    return this.formatMoney(cents).replace(/\.00(?=\D*$)/, '');
  }

  updateSavings(cents) {
    if (!this.savingsNode) return;

    this.savingsNode.textContent = cents > 0
      ? `Save ${this.formatMoneyWithoutTrailingZeros(cents)} + Free shipping`
      : 'Bundle and save';
  }
}

if (!customElements.get('r13-track-bundle')) {
  customElements.define('r13-track-bundle', R13TrackBundle);
}
